
import React, { useState, useEffect, useMemo } from 'react';
import { SPINACH_STAGES, PEPPERCRESS_STAGES, PRODUCTS, LOGO_URL } from '../constants';
import { UserProgress, Question } from '../types';

const GrowthVisualizer: React.FC<{ progress: UserProgress; activeStageId: number; tasks: string[]; isPeppercress: boolean }> = ({ progress, activeStageId, tasks, isPeppercress }) => {
  // Calculate stage-specific growth: what percentage of current tasks are done?
  const stageTasksCompleted = tasks.filter(t => progress.completedTasks.includes(t)).length;
  const stageGrowthFactor = tasks.length > 0 ? stageTasksCompleted / tasks.length : 0;
  
  const isSeed = activeStageId === 1;
  const isSprout = activeStageId === 2;
  const isLeaf = activeStageId === 3;
  const isHarvest = activeStageId === 4;

  return (
    <div className="modern-card p-6 flex flex-col items-center justify-end aspect-square relative bg-white overflow-hidden shadow-sm border-grow-green/10">
      <div className="absolute top-4 left-4 flex flex-col items-start gap-1">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-grow-green animate-pulse"></div>
          <span className="text-[9px] font-black text-grow-green/40 uppercase tracking-widest">Live Growth</span>
        </div>
        <span className="text-[8px] font-bold text-emerald-900/20 uppercase tracking-[0.2em]">Visualizing Stage {activeStageId}</span>
      </div>
      
      {/* The Pot Container */}
      <div className="relative z-20 translate-y-2 flex flex-col items-center">
        {/* Pot Top SVG */}
        <svg width="100" height="50" viewBox="0 0 100 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 5C5 2.23858 7.23858 0 10 0H90C92.7614 0 95 2.23858 95 5V10H5V5Z" fill="#3D2B1F" />
          <path d="M10 10L20 50H80L90 10H10Z" fill="#2D1E15" />
          <path d="M15 12C40 10 60 10 85 12" stroke="#4A3728" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      {/* The Dynamic Plant - Raised and z-indexed to be in front of pot */}
      <div className="absolute bottom-[54px] z-30 flex flex-col items-center origin-bottom transition-all duration-1000 ease-out">
        
        {/* PEPERCRESS VISUALS */}
        {isPeppercress ? (
          <div className="relative w-24 h-auto flex flex-col items-center">
            {isSeed && (
              <div className="flex flex-wrap justify-center w-12 gap-0.5 mb-1">
                {[...Array(Math.floor(stageGrowthFactor * 10) + 10)].map((_, i) => (
                  <div key={i} className="w-1 h-1 bg-[#5C4033] rounded-full animate-in fade-in" />
                ))}
              </div>
            )}
            
            {!isSeed && (
              <div className="relative w-full flex items-end justify-center h-[100px]">
                {/* Cluster of stems for microgreen look (18 stalks for "many") */}
                {[...Array(18)].map((_, i) => {
                  const offset = (i - 8.5) * 4;
                  const stagger = ((i * 17) % 10) - 5;
                  let height = 0;
                  // Locked height during harvest
                  if (isSprout) height = 15 + (stageGrowthFactor * 15) + stagger;
                  if (isLeaf) height = 30 + (stageGrowthFactor * 25) + stagger;
                  if (isHarvest) height = 55 + stagger; // Fixed at final height of Stage 3

                  return (
                    <div 
                      key={i} 
                      className="absolute bottom-0 origin-bottom transition-all duration-700 ease-out" 
                      style={{ left: `calc(50% + ${offset}px)` }}
                    >
                      {/* Thin Stalk */}
                      <div 
                        className="bg-emerald-700 w-0.5 rounded-full" 
                        style={{ height: `${height}px` }} 
                      />
                      
                      {/* Leaves only at the very top 1/3 for Stage 3 & 4 */}
                      {(isLeaf || isHarvest) && (
                        <div className="absolute top-0 w-full flex flex-col items-center">
                          {[...Array(isHarvest ? 6 : (Math.floor(stageGrowthFactor * 2) + 2))].map((_, li) => (
                            <div 
                              key={li}
                              className={`absolute w-2.5 h-1.5 bg-grow-green rounded-full shadow-sm animate-in zoom-in duration-500 ${li % 2 === 0 ? '-translate-x-1.5 -rotate-12' : 'translate-x-1.5 rotate-12'}`}
                              style={{ 
                                top: `${li * 3.5}px`, // Tightly clustered at the top only
                                opacity: 0.85 + (li * 0.02)
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* SPINACH VISUALS - UPDATED FOR NO BRANCHES AND CORRECT SIZING */
          <div className="relative flex flex-col items-center">
            {isSeed && (
              <div className="flex flex-col items-center mb-2">
                 <div className="flex gap-1">
                    {[...Array(3)].map((_, i) => (
                      <div 
                        key={i} 
                        className="w-2.5 h-3 bg-[#5C4033] rounded-full rotate-[15deg] animate-in fade-in shadow-sm relative overflow-hidden"
                      >
                         <div className="absolute top-0 left-0 w-1 h-1 bg-white/20 rounded-full m-0.5"></div>
                      </div>
                    ))}
                 </div>
                 {/* Small root emerging as they check off tasks */}
                 <div 
                  className="w-0.5 bg-emerald-100 rounded-full transition-all duration-1000 origin-top"
                  style={{ height: `${stageGrowthFactor * 15}px`, opacity: stageGrowthFactor }}
                 />
              </div>
            )}

            {isSprout && (
              <div className="relative flex flex-col items-center">
                {/* Thin stem */}
                <div 
                  className="bg-[#A7C957] w-0.5 rounded-full origin-bottom transition-all duration-700"
                  style={{ height: `${15 + (stageGrowthFactor * 15)}px` }}
                />
                {/* Cotyledons - slender like image */}
                <div className="absolute top-0 flex gap-[1px]">
                  <div 
                    className="w-5 h-2.5 bg-[#6A994E] rounded-[20%_80%_20%_80%] -rotate-45 origin-right transition-all duration-500"
                    style={{ transform: `scale(${0.6 + stageGrowthFactor * 0.4}) rotate(${-30 - stageGrowthFactor * 15}deg)` }}
                  />
                  <div 
                    className="w-5 h-2.5 bg-[#6A994E] rounded-[80%_20%_80%_20%] rotate-45 origin-left transition-all duration-500"
                    style={{ transform: `scale(${0.6 + stageGrowthFactor * 0.4}) rotate(${30 + stageGrowthFactor * 15}deg)` }}
                  />
                </div>
              </div>
            )}

            {isLeaf && (
              <div className="relative flex flex-col items-center">
                {/* Main Stem - clearly visible and lifting foliage */}
                <div 
                  className="bg-[#386641] w-2 rounded-full origin-bottom transition-all duration-700"
                  style={{ height: `${30 + (stageGrowthFactor * 5)}px` }}
                />
                {/* Leaves - Slightly smaller and connected directly to the top of the stem */}
                <div className="absolute bottom-full translate-y-2 flex justify-center w-32">
                   {[...Array(Math.floor(stageGrowthFactor * 1) + 3)].map((_, i) => {
                     const angleMap = [ -60, 60, -25, 25 ];
                     const angle = angleMap[i] || 0;
                     // Requested: slightly smaller leaves in stage 3
                     const scale = 0.7 + (stageGrowthFactor * 0.1);
                     return (
                       <div key={i} className="absolute origin-bottom transition-all duration-700" style={{ transform: `rotate(${angle}deg)` }}>
                          <div 
                            className="absolute -top-10 -left-6 w-12 h-14 bg-grow-green rounded-[50%_50%_45%_45%] shadow-sm border border-emerald-800/20 animate-in zoom-in"
                            style={{ 
                              transform: `scale(${scale})`,
                              opacity: 0.95,
                              zIndex: 10 - i
                            }}
                          >
                            <div className="absolute left-1/2 bottom-0 w-[1px] h-3/4 bg-emerald-900/10 -translate-x-1/2"></div>
                          </div>
                       </div>
                     );
                   })}
                </div>
              </div>
            )}

            {isHarvest && (
              <div className="relative flex flex-col items-center">
                {/* Main Central Stem */}
                <div 
                  className="bg-[#386641] w-2.5 rounded-full origin-bottom"
                  style={{ height: '35px' }}
                />
                {/* Exactly 4 Larger Leaves - Connected directly to the stem top */}
                <div className="absolute bottom-full translate-y-3 flex justify-center w-40">
                   {[...Array(4)].map((_, i) => {
                     const angles = [-75, 75, -25, 25];
                     const angle = angles[i] || 0;
                     // Larger size for harvest stage
                     const scale = 1.05 + (stageGrowthFactor * 0.1);
                     return (
                       <div key={i} className="absolute origin-bottom transition-all duration-700" style={{ transform: `rotate(${angle}deg)` }}>
                          <div 
                            className="absolute -top-14 -left-7 w-14 h-16 bg-grow-green rounded-[48%_48%_45%_45%] shadow-md border border-emerald-900/10 animate-in bounce-in"
                            style={{ 
                              transform: `scale(${scale})`,
                              opacity: 0.98,
                              zIndex: 20 - i
                            }}
                          >
                            <div className="absolute left-1/2 bottom-0 w-[1px] h-4/5 bg-emerald-900/10 -translate-x-1/2"></div>
                          </div>
                       </div>
                     );
                   })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Completion Flare */}
      {stageGrowthFactor === 1 && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="text-2xl animate-ping text-grow-green/30">✨</div>
        </div>
      )}
    </div>
  );
};

const QuizInterface: React.FC<{
  questions: Question[];
  onFinish: (score: number) => void;
}> = ({ questions, onFinish }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(false);

  const currentQuestion = questions[currentIndex];

  const handleOptionSelect = (idx: number) => {
    if (answered) return;
    setSelectedOption(idx);
    setAnswered(true);
    if (idx === currentQuestion.correctIndex) {
      setScore(prev => prev + 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedOption(null);
      setAnswered(false);
    } else {
      onFinish(score);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-grow-green/50">
        <span>Question {currentIndex + 1} of {questions.length}</span>
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-bold serif-heading !text-white leading-snug">
          {currentQuestion.question}
        </h3>
        <div className="grid gap-2">
          {currentQuestion.options.map((option, idx) => {
            let styles = "bg-emerald-800/50 border-emerald-700/50 text-white/70 hover:bg-white/5";
            if (answered) {
              if (idx === currentQuestion.correctIndex) {
                styles = "bg-grow-green border-grow-green text-white";
              } else if (selectedOption === idx) {
                styles = "bg-red-500/20 border-red-500/50 text-red-200";
              } else {
                styles = "bg-emerald-900/40 border-white/5 text-white/30";
              }
            }

            return (
              <button
                key={idx}
                disabled={answered}
                onClick={() => handleOptionSelect(idx)}
                className={`w-full text-left px-5 py-3 rounded-xl border font-bold text-sm transition-all ${styles}`}
              >
                {option}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button
          disabled={!answered}
          onClick={handleNext}
          className={`px-8 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${
            answered 
              ? 'bg-white text-emerald-900 shadow-lg' 
              : 'bg-emerald-800 text-emerald-600 cursor-not-allowed'
          }`}
        >
          {currentIndex === questions.length - 1 ? 'Finish Assessment' : 'Next Question'}
        </button>
      </div>
    </div>
  );
};

const Learn: React.FC = () => {
  const [progress, setProgress] = useState<UserProgress>(() => {
    const saved = localStorage.getItem('growLocalProgress');
    return saved ? JSON.parse(saved) : { completedStages: [], completedTasks: [], completedQuizzes: [], passedQuizStages: [] };
  });

  const [selectedKitId, setSelectedKitId] = useState<string | null>(() => {
    return localStorage.getItem('growLocalSelectedKit');
  });

  const [gardeningLevel, setGardeningLevel] = useState<string | null>(() => {
    return localStorage.getItem('growLocalGardeningLevel');
  });

  const [activeStageId, setActiveStageId] = useState<number>(() => {
    const saved = localStorage.getItem('growLocalActiveStage');
    return saved ? parseInt(saved, 10) : 1;
  });

  const [quizState, setQuizState] = useState<'intro' | 'active' | 'results'>('intro');
  const [currentScore, setCurrentScore] = useState(0);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);

  const stages = useMemo(() => {
    if (selectedKitId === 'peppercress-kit') return PEPPERCRESS_STAGES;
    return SPINACH_STAGES;
  }, [selectedKitId]);

  // Persist Progress
  useEffect(() => {
    localStorage.setItem('growLocalProgress', JSON.stringify(progress));
  }, [progress]);

  // Persist Selected Kit
  useEffect(() => {
    if (selectedKitId) {
      localStorage.setItem('growLocalSelectedKit', selectedKitId);
    } else {
      localStorage.removeItem('growLocalSelectedKit');
    }
  }, [selectedKitId]);

  // Persist Gardening Level
  useEffect(() => {
    if (gardeningLevel) {
      localStorage.setItem('growLocalGardeningLevel', gardeningLevel);
    } else {
      localStorage.removeItem('growLocalGardeningLevel');
    }
  }, [gardeningLevel]);

  // Persist Active Stage
  useEffect(() => {
    localStorage.setItem('growLocalActiveStage', activeStageId.toString());
  }, [activeStageId]);

  useEffect(() => {
    setQuizState('intro');
    setCurrentScore(0);
  }, [activeStageId]);

  const activeStage = stages.find(s => s.id === activeStageId)!;

  // Calculate Overall Progress
  const totalAllTasks = useMemo(() => stages.reduce((acc, s) => acc + s.tasks.length, 0), [stages]);
  const totalAllQuizzes = stages.length;
  const totalOverallObjectives = totalAllTasks + totalAllQuizzes;

  const completedAllTasksCount = progress.completedTasks.length;
  const completedAllQuizzesCount = (progress.passedQuizStages || []).length;
  const completedOverallObjectives = completedAllTasksCount + completedAllQuizzesCount;

  const overallProgressPercent = Math.round((completedOverallObjectives / totalOverallObjectives) * 100);

  const isUnlocked = (stageId: number) => {
    if (stageId === 1) return true;
    return progress.completedStages.includes(stageId - 1);
  };

  const checkModuleCompletion = (updatedTasks: string[], updatedPassedQuizzes: number[]) => {
    const stageTasks = activeStage.tasks;
    const allDone = stageTasks.every(t => updatedTasks.includes(t));
    const quizPassed = updatedPassedQuizzes.includes(activeStageId);
    
    let newStages = [...progress.completedStages];
    if (allDone && quizPassed && !newStages.includes(activeStageId)) {
      newStages.push(activeStageId);
    } else if (!allDone || !quizPassed) {
      newStages = newStages.filter(id => id !== activeStageId);
    }
    return newStages;
  };

  const toggleTask = (task: string) => {
    setProgress(prev => {
      const isCompleted = prev.completedTasks.includes(task);
      const newTasks = isCompleted 
        ? prev.completedTasks.filter(t => t !== task)
        : [...prev.completedTasks, task];
      
      const newCompletedStages = checkModuleCompletion(newTasks, prev.passedQuizStages || []);
      return { ...prev, completedTasks: newTasks, completedStages: newCompletedStages };
    });
  };

  const handleQuizFinish = (score: number) => {
    setCurrentScore(score);
    setQuizState('results');
    
    if (score >= 3) {
      setProgress(prev => {
        const currentPassed = prev.passedQuizStages || [];
        const newPassed = currentPassed.includes(activeStageId) ? currentPassed : [...currentPassed, activeStageId];
        const newCompletedStages = checkModuleCompletion(prev.completedTasks, newPassed);
        return { ...prev, passedQuizStages: newPassed, completedStages: newCompletedStages };
      });
    }
  };

  const handleSwitchKit = () => {
    setSelectedKitId(null);
    setGardeningLevel(null);
    localStorage.removeItem('growLocalSelectedKit');
    localStorage.removeItem('growLocalGardeningLevel');
    localStorage.removeItem('growLocalProgress');
    localStorage.removeItem('growLocalActiveStage');
    setProgress({ completedStages: [], completedTasks: [], completedQuizzes: [], passedQuizStages: [] });
    setActiveStageId(1);
    setShowSwitchConfirm(false);
  };

  // 1. Kit Selection Screen
  if (!selectedKitId) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 space-y-16 animate-in fade-in duration-700">
        <div className="text-center space-y-6">
          <h2 className="text-6xl md:text-[7.5rem] font-black leading-[0.85] tracking-tighter serif-heading !text-grow-green drop-shadow-sm">
            Choose your <span className="italic">Path</span>
          </h2>
          <p className="text-xl md:text-2xl text-emerald-900/60 font-medium">Choose which kit you are growing to start your learning journey.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {PRODUCTS.map(p => (
            <button 
              key={p.id}
              onClick={() => setSelectedKitId(p.id)}
              className="modern-card p-12 text-left space-y-6 border-2 border-transparent hover:border-grow-green transition-all group"
            >
              <div className="aspect-video rounded-2xl overflow-hidden shadow-lg">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              </div>
              <div className="space-y-2">
                <h3 className="text-3xl font-black serif-heading">{p.name}</h3>
                <p className="text-emerald-900/60 font-medium">{p.description}</p>
              </div>
              <div className="btn-primary inline-block">Start Learning</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // 2. Gardening Level Selection Screen
  if (selectedKitId && !gardeningLevel) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 space-y-16 animate-in fade-in duration-700">
        <div className="text-center space-y-6">
          <h2 className="text-6xl md:text-[7.5rem] font-black leading-[0.85] tracking-tighter serif-heading !text-grow-green drop-shadow-sm">
            Your <span className="italic">Experience</span>
          </h2>
          <p className="text-xl md:text-2xl text-emerald-900/60 font-medium">Tell us your grade level to personalize your gardening journey.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <button 
            onClick={() => setGardeningLevel('junior')}
            className="modern-card p-12 text-center space-y-6 border-2 border-transparent hover:border-grow-green transition-all group bg-white"
          >
            <div className="text-7xl mb-4 group-hover:scale-110 transition-transform">🎒</div>
            <div className="space-y-2">
              <h3 className="text-3xl font-black serif-heading">Beginner Gardener</h3>
            </div>
            <div className="btn-primary w-full text-sm">Select Beginner</div>
          </button>
          
          <button 
            onClick={() => setGardeningLevel('senior')}
            className="modern-card p-12 text-center space-y-6 border-2 border-transparent hover:border-grow-green transition-all group bg-white"
          >
            <div className="text-7xl mb-4 group-hover:scale-110 transition-transform">🎓</div>
            <div className="space-y-2">
              <h3 className="text-3xl font-black serif-heading">Advanced Gardener</h3>
            </div>
            <div className="btn-primary w-full text-sm">Select Advanced</div>
          </button>
        </div>
        
        <div className="flex justify-center pt-8">
          <button 
            onClick={() => setSelectedKitId(null)}
            className="text-grow-green/40 hover:text-grow-green font-bold text-sm underline underline-offset-4 decoration-2"
          >
            ← Back to kit selection
          </button>
        </div>
      </div>
    );
  }

  // 3. Curriculum View
  const quizPassedInStage = (progress.passedQuizStages || []).includes(activeStageId);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {/* Switch Confirmation Modal */}
      {showSwitchConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center px-6">
          <div 
            className="absolute inset-0 bg-emerald-900/60 backdrop-blur-sm animate-in fade-in"
            onClick={() => setShowSwitchConfirm(false)}
          ></div>
          <div className="relative modern-card p-10 bg-white max-w-sm w-full space-y-8 animate-in zoom-in slide-in-from-bottom-4 shadow-2xl">
            <div className="space-y-4 text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center text-3xl mx-auto mb-2">⚠️</div>
              <h3 className="text-2xl font-bold serif-heading">Are you sure?</h3>
              <p className="text-emerald-900/60 font-medium leading-relaxed">
                Switching kits will <span className="text-red-500 font-black">reset your current progress</span>. This action cannot be undone.
              </p>
            </div>
            <div className="flex flex-col gap-3 pt-2">
              <button 
                onClick={handleSwitchKit}
                className="w-full py-4 rounded-xl bg-red-500 text-white font-black uppercase tracking-widest text-[10px] shadow-lg shadow-red-500/20 hover:bg-red-600 transition-all"
              >
                Reset & Switch
              </button>
              <button 
                onClick={() => setShowSwitchConfirm(false)}
                className="w-full py-4 rounded-xl font-bold text-sm text-grow-green hover:bg-emerald-50 transition-all"
              >
                Keep Growing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="space-y-3 py-6 mb-8 max-w-4xl mx-auto">
        <div className="flex justify-between items-end px-1">
          <span className="text-[10px] font-black uppercase tracking-widest text-grow-green opacity-50">Plant Growth</span>
          <span className="text-sm font-black text-grow-green">{overallProgressPercent}% Completed</span>
        </div>
        <div className="w-full h-4 bg-emerald-100/50 rounded-full overflow-hidden border border-emerald-100">
          <div 
            className="h-full bg-grow-green transition-all duration-1000 ease-out relative"
            style={{ width: `${overallProgressPercent}%` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
          </div>
        </div>
        <p className="text-[10px] font-bold text-emerald-900/30 text-center uppercase tracking-[0.2em]">
          {completedOverallObjectives} of {totalOverallObjectives} total objectives met across all growth stages
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar */}
        <aside className="lg:w-1/4 space-y-6">
          <GrowthVisualizer 
            progress={progress} 
            activeStageId={activeStageId} 
            tasks={activeStage.tasks} 
            isPeppercress={selectedKitId === 'peppercress-kit'}
          />

          <div className="modern-card p-4 bg-white border border-emerald-100 flex items-center gap-3">
             <div className="text-2xl">{gardeningLevel === 'junior' ? '🎒' : '🎓'}</div>
             <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-grow-green/40">Current Path</p>
                <p className="text-xs font-bold text-emerald-900 capitalize">{gardeningLevel === 'junior' ? 'Beginner' : 'Advanced'} Gardener</p>
             </div>
          </div>

          <nav className="space-y-3">
            {stages.map((stage) => {
              const unlocked = isUnlocked(stage.id);
              const isCompleted = progress.completedStages.includes(stage.id);
              return (
                <button
                  key={stage.id}
                  disabled={!unlocked}
                  onClick={() => setActiveStageId(stage.id)}
                  className={`w-full flex items-center justify-between p-5 rounded-2xl transition-all border-2 ${
                    activeStageId === stage.id 
                      ? 'bg-grow-green text-white border-grow-green shadow-lg' 
                      : unlocked 
                        ? 'bg-white border-transparent text-grow-green/40 hover:bg-emerald-50' 
                        : 'bg-emerald-100/30 border-transparent text-emerald-900/10 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className={`text-2xl ${!unlocked ? 'grayscale opacity-50' : ''}`}>{unlocked ? stage.emoji : '🔒'}</span>
                    <span className="font-bold text-sm">{stage.title}</span>
                  </div>
                  {isCompleted && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white shadow-sm"></div>
                  )}
                </button>
              );
            })}
          </nav>

          <button 
            onClick={() => setShowSwitchConfirm(true)}
            className="w-full p-5 rounded-2xl border-2 border-dashed border-emerald-200 text-grow-green/40 font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 hover:border-grow-green hover:text-grow-green transition-all"
          >
            Switch Gardening Kit
          </button>
        </aside>

        {/* Main Content */}
        <main className="flex-1 space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="modern-card p-10 space-y-4">
              <h4 className="text-[10px] font-black uppercase tracking-widest text-grow-green opacity-60">The Science</h4>
              <p className="text-emerald-900/70 font-medium leading-relaxed">
                {activeStage.whatIsHappening}
              </p>
            </div>
            <div className="modern-card p-10 space-y-4">
              <h4 className="text-[10px] font-black uppercase tracking-widest text-grow-green opacity-60">The Directive</h4>
              <p className="text-emerald-900/70 font-medium leading-relaxed">
                {activeStage.whatToDo}
              </p>
            </div>
          </div>

          {/* Video Section */}
          <div className="space-y-12">
            {/* Kit Unboxing Video */}
            {selectedKitId === 'spinach-kit' && (
              <div className="space-y-4">
                <h4 className="text-[22px] leading-[1.1] sm:leading-[5px] font-black uppercase tracking-widest text-grow-green opacity-60 px-2">
                  Kit Unboxing <br className="sm:hidden" /> Video
                </h4>
                <div className="modern-card overflow-hidden aspect-video shadow-2xl relative border-8 border-white bg-black">
                  <a 
                    href="https://youtu.be/0ROH6v_zgEw?si=VGnPx1hSd5prsS1f" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full h-full block group relative"
                  >
                    <img 
                      src="https://i.ytimg.com/vi/0ROH6v_zgEw/maxresdefault.jpg" 
                      alt="Watch Spinach Kit Unboxing" 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100"
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center text-white shadow-2xl transform transition-all group-hover:scale-110 group-active:scale-95">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 ml-1">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                    <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end pointer-events-none">
                      <div className="bg-black/60 backdrop-blur-md px-4 py-2 rounded-xl text-white text-[10px] font-black uppercase tracking-widest border border-white/10">
                        Watch Spinach Unboxing
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            )}

            {/* Kit Information Video */}
            <div className="space-y-4">
              <h4 className="text-[22px] leading-[1.1] sm:leading-[5px] font-black uppercase tracking-widest text-grow-green opacity-60 px-2">
                Kit Information <br className="sm:hidden" /> Video
              </h4>
              <div className="modern-card overflow-hidden aspect-video shadow-2xl relative border-8 border-white bg-black">
                {selectedKitId === 'spinach-kit' ? (
                  <a 
                    href="https://www.youtube.com/watch?v=nDYPWtCtP88" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full h-full block group relative"
                  >
                    <img 
                      src="https://i.ytimg.com/vi/nDYPWtCtP88/maxresdefault.jpg" 
                      alt="Watch Spinach Growth Video Tutorial" 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100"
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center text-white shadow-2xl transform transition-all group-hover:scale-110 group-active:scale-95">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 ml-1">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                    <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end pointer-events-none">
                      <div className="bg-black/60 backdrop-blur-md px-4 py-2 rounded-xl text-white text-[10px] font-black uppercase tracking-widest border border-white/10">
                        Watch Spinach Tutorial
                      </div>
                    </div>
                  </a>
                ) : (
                  <a 
                    href="https://www.youtube.com/watch?v=KXFGN-1uCmk&t=7s" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full h-full block group relative"
                  >
                    <img 
                      src="https://i.ytimg.com/vi/KXFGN-1uCmk/maxresdefault.jpg" 
                      alt="Watch Peppercress Growth Video Tutorial" 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100"
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center text-white shadow-2xl transform transition-all group-hover:scale-110 group-active:scale-95">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 ml-1">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                    <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end pointer-events-none">
                      <div className="bg-black/60 backdrop-blur-md px-4 py-2 rounded-xl text-white text-[10px] font-black uppercase tracking-widest border border-white/10">
                        Watch Peppercress Tutorial
                      </div>
                    </div>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Checklist and Quiz Section */}
          <div className="grid md:grid-cols-2 gap-8">
            <div className="modern-card p-10 bg-emerald-900 text-white space-y-8">
              <h3 className="text-2xl font-bold serif-heading !text-white">Module Checklist</h3>
              <div className="space-y-4">
                {activeStage.tasks.map((task) => (
                  <button 
                    key={task}
                    onClick={() => toggleTask(task)}
                    className={`w-full p-5 rounded-2xl flex items-center gap-4 border-2 transition-all text-left ${
                      progress.completedTasks.includes(task) 
                        ? 'bg-grow-green border-grow-green' 
                        : 'bg-emerald-800 border-emerald-700 hover:border-white/20'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center text-xs font-black transition-all ${
                      progress.completedTasks.includes(task) ? 'bg-white border-white text-emerald-900' : 'border-emerald-600'
                    }`}>
                      {progress.completedTasks.includes(task) && '✓'}
                    </div>
                    <span className={`font-bold text-sm ${progress.completedTasks.includes(task) ? 'opacity-60 line-through' : ''}`}>
                      {task}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="modern-card p-10 bg-emerald-900 text-white space-y-8 min-h-[400px]">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold serif-heading !text-white">Knowledge Check</h3>
                {quizPassedInStage && (
                  <span className="text-[9px] font-black uppercase tracking-widest bg-grow-green px-3 py-1 rounded-full animate-pulse">Completed</span>
                )}
              </div>

              {quizState === 'intro' ? (
                <div className="space-y-6 flex flex-col justify-center h-full text-center">
                  <div className="text-5xl">📖</div>
                  <p className="text-emerald-100/60 font-medium leading-relaxed">
                    Test your understanding of the {activeStage.title} phase to unlock the next stage. <br/>
                    <span className="text-grow-green font-bold">(Require 3/4 to pass)</span>
                  </p>
                  <button 
                    onClick={() => setQuizState('active')}
                    className="w-full py-4 rounded-xl bg-white text-emerald-900 font-black uppercase tracking-widest text-[10px] hover:shadow-xl transition-all"
                  >
                    {quizPassedInStage ? 'Retake Assessment' : 'Begin Assessment'}
                  </button>
                </div>
              ) : quizState === 'active' ? (
                <QuizInterface 
                  questions={(gardeningLevel === 'senior' && activeStage.seniorQuiz) ? activeStage.seniorQuiz : activeStage.quiz} 
                  onFinish={handleQuizFinish} 
                />
              ) : (
                <div className="text-center space-y-6 flex flex-col justify-center h-full">
                  <div className="text-6xl">{currentScore >= 3 ? '🎉' : '🌱'}</div>
                  <h4 className="text-3xl font-black serif-heading !text-white">
                    {currentScore}/4 Correct
                  </h4>
                  <p className="text-emerald-100/60 font-medium">
                    {currentScore >= 3 
                      ? "Great job! You've unlocked the next protocol." 
                      : "Review the Science and Directive to unlock the next stage."}
                  </p>
                  {currentScore < 3 ? (
                    <button 
                      onClick={() => setQuizState('active')}
                      className="w-full py-4 rounded-xl bg-emerald-800 text-white font-black uppercase tracking-widest text-[10px] border border-emerald-700"
                    >
                      Try Again
                    </button>
                  ) : (
                    <button 
                      onClick={() => setQuizState('intro')}
                      className="w-full py-4 rounded-xl bg-white text-emerald-900 font-black uppercase tracking-widest text-[10px]"
                    >
                      Back to Overview
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {!progress.completedStages.includes(activeStageId) && activeStageId < stages.length && (
            <div className="p-6 bg-emerald-50 border border-emerald-100 rounded-[32px] text-center">
              <p className="text-sm font-bold text-emerald-900/40 uppercase tracking-widest">
                Complete all checklist items and score 3/4 on the quiz to unlock <span className="text-grow-green font-black">{stages[activeStageId].title}</span>
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Learn;
